import React, { useState } from "react";
import { Box, Typography, Container, Grid, Paper, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import PetsIcon from "@mui/icons-material/Pets";
import WeekendIcon from "@mui/icons-material/Weekend";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { ServiceDialog } from "./ServiceDialog.tsx";
import dagopvang from "../assets/images/dagopvang.jpg";
import weekendopvang from "../assets/images/weekendopvang.jpg";
import vakantieopvang from "../assets/images/vakantieopvang.jpg";

// Styled components
const StyledServicesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledServicesContainer = styled(Container)(({ theme }) => ({
  maxWidth: 'lg',
}));

const StyledServicesTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const StyledServiceCard = styled(Paper)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'transform 0.3s, box-shadow 0.3s',
  backgroundColor: $isDarkMode ? '#3D3D3D' : theme.palette.background.paper,
  color: $isDarkMode ? '#F0F0F0' : theme.palette.text.primary,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
    backgroundColor: $isDarkMode ? '#4A4A4A' : theme.palette.background.paper,
  },
}));

const StyledServiceIcon = styled(Box)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  '& svg': {
    color: $isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
  },
}));

const StyledServiceTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledServiceDescription = styled(Typography)(({ theme }) => ({
  // Add any specific styling for service descriptions
}));

// Extended service type with image and detailed description
interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  detailedDescription: string;
  image: any;
}

export const ServicesSection: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleOpenDialog = (service: Service) => {
    setSelectedService(service);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const services: Service[] = [
    {
      title: "Feestdagopvang",
      description: "Ook tijdens feestdagen kan een hond gezellig bij Pippaya komen logeren.",
      icon: <PetsIcon fontSize="large" aria-hidden="true" />,
      detailedDescription: "Terwijl thuis de kerstballen, oliebollen of andere feestelijkheden tevoorschijn komen, vieren wij hier ons eigen hondenfeestje. Geen verplichte kerstmutsen — tenzij iemand daar toevallig héél charmant mee staat 😂 — maar wel wandelen, gezelligheid, aandacht en natuurlijk iets lekkers. Feestdagopvang kan eventueel inclusief één overnachting, zodat de baasjes zonder haast van hun dag of avond kunnen genieten. Want feestdag of gewone maandag: bij Pippaya hoort een logeerhond er gewoon bij. ❤️🐾",
      image: dagopvang,
    },
    {
      title: "Weekendopvang",
      description: "Een weekendje weg? Dan maken we er hier gewoon óók een leuk weekend van.",
      icon: <WeekendIcon fontSize="large" aria-hidden="true" />,
      detailedDescription: "Vanaf het moment van binnenkomst hoort de hond bij de club. We wandelen, spelen, luieren en kijken vooral waar die specifieke hond blij van wordt. En tegen de tijd dat de baasjes weer voor de deur staan, is de kans groot dat er eerst nog even uitgebreid moet worden uitgerekt voordat iemand mee naar huis wil. 😂🐾",
      image: weekendopvang,
    },
    {
      title: "Vakantieopvang",
      description: "Ga vooral genieten. Wij zorgen hier voor de hondenvakantie.",
      icon: <FlightTakeoffIcon fontSize="large" aria-hidden="true" />,
      detailedDescription: "Polderwandelingen, zwemmen, spelen, modderpoten, lekkere snackjes, knuffels en natuurlijk voldoende rust. Omdat Pippaya bewust kleinschalig is, leren we onze logeerhonden echt kennen en kunnen we rekening houden met hun eigen gewoontes en karakter. Het enige risico? Dat je bij thuiskomst niet helemaal zeker weet wie nou eigenlijk de leukste vakantie heeft gehad. 😉",
      image: vakantieopvang,
    },
  ];

  return (
    <StyledServicesSection
      id="services"
      component="section"
      aria-labelledby="services-heading"
    >
      <StyledServicesContainer>
        <StyledServicesTitle 
          variant="h3" 
          component="h2" 
          id="services-heading"
        >
          Onze Diensten
        </StyledServicesTitle>

        <Grid container spacing={4} role="list">
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={index} role="listitem">
              <StyledServiceCard
                $isDarkMode={isDarkMode}
                elevation={3}
                onClick={() => handleOpenDialog(service)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleOpenDialog(service);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Meer informatie over ${service.title}`}
              >
                <StyledServiceIcon $isDarkMode={isDarkMode}>
                  {service.icon}
                </StyledServiceIcon>
                <StyledServiceTitle variant="h5" component="h3">
                  {service.title}
                </StyledServiceTitle>
                <StyledServiceDescription>{service.description}</StyledServiceDescription>
              </StyledServiceCard>
            </Grid>
          ))}
        </Grid>
      </StyledServicesContainer>

      {/* Service Dialog */}
      <ServiceDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        service={selectedService}
      />
    </StyledServicesSection>
  );
};
